import { Button, Input } from "@aix/ui"
import { X, Sparkles } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import dayjs from "dayjs"
import { trpc } from "../../../lib/trpc"

interface AIEnhancePlanModalProps {
  coachId: string
  coachName: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function AIEnhancePlanModal({
  coachId,
  coachName,
  isOpen,
  onClose,
  onSuccess,
}: AIEnhancePlanModalProps) {
  const [weekStartDate, setWeekStartDate] = useState(dayjs().startOf("week").format("YYYY-MM-DD"))
  const [weekEndDate, setWeekEndDate] = useState(dayjs().endOf("week").format("YYYY-MM-DD"))
  const [brainDump, setBrainDump] = useState("")

  const enhanceMutation = trpc.plans.weeklyPlans.generate.useMutation({
    onSuccess: () => {
      toast.success("AI-enhanced plan created successfully!")
      onSuccess()
      onClose()
      // Reset form
      setWeekStartDate(dayjs().startOf("week").format("YYYY-MM-DD"))
      setWeekEndDate(dayjs().endOf("week").format("YYYY-MM-DD"))
      setBrainDump("")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create AI-enhanced plan")
    },
  })

  const handleSubmit = () => {
    if (!brainDump.trim()) {
      toast.error("Please provide your brain dump for the week")
      return
    }

    if (!weekStartDate || !weekEndDate) {
      toast.error("Please select week start and end dates")
      return
    }

    // Calculate week number
    const startDate = new Date(weekStartDate)
    const startOfYear = new Date(startDate.getFullYear(), 0, 1)
    const weekNumber = Math.ceil(
      ((startDate.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7,
    )

    enhanceMutation.mutate({
      coachId,
      weekNumber,
      weekStartDate,
      weekEndDate,
      context: brainDump, // This is the user's brain dump that AI will structure
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">AI Enhance Mode</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Brain dump your week and {coachName} will structure it for you
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">How AI Enhance Works</h3>
                <p className="text-sm text-muted-foreground">
                  Simply dump all your thoughts, tasks, and goals for the week in the text area below. AI will analyze
                  your input and create a structured weekly plan with clear expectations and daily structure.
                </p>
              </div>
            </div>
          </div>

          {/* Week Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Week Start Date</label>
              <Input type="date" value={weekStartDate} onChange={(e) => setWeekStartDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Week End Date</label>
              <Input type="date" value={weekEndDate} onChange={(e) => setWeekEndDate(e.target.value)} />
            </div>
          </div>

          {/* Brain Dump Area */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Your Brain Dump</label>
            <textarea
              value={brainDump}
              onChange={(e) => setBrainDump(e.target.value)}
              placeholder="Example: This week I need to finish the project proposal, have 3 client meetings on Tuesday and Thursday, go to the gym Monday/Wed/Friday, prepare for the quarterly review, and start working on the new feature... just dump everything here!"
              className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
              rows={12}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {brainDump.length} characters • Be as detailed or brief as you want
            </p>
          </div>

          {/* Tips */}
          <div className="bg-secondary rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2 text-sm">Tips for better results:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Mention specific days if you have time-bound tasks</li>
              <li>• Include priorities or what&apos;s most important this week</li>
              <li>• Note any constraints (limited time, energy, etc.)</li>
              <li>• Don&apos;t worry about structure - just get it all out!</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={enhanceMutation.isLoading}
            disabled={enhanceMutation.isLoading || !brainDump.trim()}
            className="flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Generate Structured Plan
          </Button>
        </div>
      </div>
    </div>
  )
}
